import { useEffect, useState } from "react";
import { Card, Badge, Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import NavigationBar from '../navbar/NavigationBar';
import Nav from 'react-bootstrap/Nav';
import Alert from 'react-bootstrap/Alert';

function formatDate(isoOrEpoch) {
    // If input is a number or a numeric string, treat as epoch
    let date;
    if (typeof isoOrEpoch === "number" || /^\d+$/.test(isoOrEpoch)) {
        date = new Date(Number(isoOrEpoch) * (String(isoOrEpoch).length === 10 ? 1000 : 1));
    } else {
        date = new Date(isoOrEpoch);
    }

    // return date.toLocaleString(); // 7/2/2025, 5:00:00 AM
    // return date.toLocaleDateString('en-US'); // M/D/YYYY
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD
}

function getMonthYear(isoString) {
    const date = new Date(isoString);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

function buildCategoryHierarchy(blogs) {
    const categoryMap = {};
    blogs.forEach(blog => {
        let cat = blog.formCategory;
        if (!categoryMap[cat]) categoryMap[cat] = [];
        categoryMap[cat].push(blog);
    });
    // Sort categories alphabetically
    return Object.keys(categoryMap)
        .sort()
        .map(cat => ({
            category: cat,
            blogs: categoryMap[cat].sort((a, b) => a.formTitle.localeCompare(b.formTitle))
        }));
}


function BlogsTimeline() {
    const timeoutInMilliseconds = 5000;
    const [latestBlogsLoading, setLatestBlogsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [formPageSize, setFormPageSize] = useState(10);
    const [continuationToken, setContinuationToken] = useState(undefined);
    const [retrievedBlogs, setRetrievedBlogs] = useState([]);
    const [groupedBlogs, setGroupedBlogs] = useState([]);
    const [categoryHierarchy, setCategoryHierarchy] = useState([]);
    const [isAlertInfoVisible, setIsAlertInfoVisible] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const timeoutId = setTimeout(() => {
                setErrorMessage(`Timeout! Back-end service took more than ${timeoutInMilliseconds / 1000} seconds.`);
                setLatestBlogsLoading(false);
            }, timeoutInMilliseconds);

            setLatestBlogsLoading(true);
            fetch(process.env.REACT_APP_BLOGS_API_URL + `?pagination=${formPageSize}${continuationToken ? `&continuationToken=${encodeURIComponent(continuationToken)}` : ''}`)
                .then(response => {
                    if (response.ok) {
                        response.text().then(value => {
                            let responseValue = JSON.parse(value);
                            if (responseValue && responseValue.resources) {
                                // Sort blogs by createdAt descending
                                const sortedBlogs = [...responseValue.resources].sort((a, b) => new Date(b.formCreateDate) - new Date(a.formCreateDate));

                                // Group blogs by month-year for timeline
                                let lastMonthYear = "";
                                const newGroupedBlogs = [];
                                sortedBlogs.forEach(blog => {
                                    const monthYear = getMonthYear(blog.formCreateDate);
                                    if (monthYear !== lastMonthYear) {
                                        newGroupedBlogs.push({ type: "month", value: monthYear });
                                        lastMonthYear = monthYear;
                                    }
                                    newGroupedBlogs.push({ type: "blog", value: blog });
                                });

                                setRetrievedBlogs(responseValue.resources);
                                setGroupedBlogs(newGroupedBlogs);
                                setCategoryHierarchy(buildCategoryHierarchy(responseValue.resources));
                            }

                            // Update the continuation token for the next page
                            setContinuationToken(responseValue.nextPageToken);
                            // If nextPageToken is null, you are at the last page

                            let requestCharge = responseValue.requestCharge;
                            if (requestCharge) {
                                console.log(`requestCharge: ${requestCharge}`);
                            }
                        });
                    } else if (response.status === 300) {
                        alert('Blogs might actually exist, but it was wrongly cached. Try resetting your browser.');
                        setRetrievedBlogs([]);
                    } else {
                        setRetrievedBlogs([]);
                        response.text().then(value => {
                            setErrorMessage(value);
                        });
                    }

                    clearTimeout(timeoutId);
                    setLatestBlogsLoading(false);
                })
                .catch(error => {
                    setLatestBlogsLoading(false);
                    clearTimeout(timeoutId);
                    console.log(error)
                });
        };

        fetchData();

        const blogsBgElement = document.querySelector('.blogs-timline-bg');
        if (blogsBgElement) blogsBgElement.classList.add('is-visible');

    }, [continuationToken, formPageSize]);

    const navigate = useNavigate();
    function navigateTo(idx, theBlog) {
        let slug = theBlog.formTitle.toLowerCase().replaceAll(' ', '_');
        // let encodedSlug = encodeURIComponent(slug);

        var parser = document.createElement('a');
        let currLocRef = window.location.href;

        if (currLocRef.includes('blogs-timeline')) {
            currLocRef = currLocRef.replace('blogs-timeline', 'blogs/' + theBlog.formCategory);
        }

        if (currLocRef.endsWith('/')) {
            parser.href = currLocRef + `${slug}`;
        } else {
            parser.href = currLocRef + `/${slug}`;
        }

        navigate(parser.pathname, {
            replace: false, state: {
                data: theBlog,
                lastUpdated: theBlog._ts
            }
        });
    }

    return (
        <div className="blogs-timline-bg">
            <NavigationBar></NavigationBar>
            {/* <Container style={{ marginTop: "1rem", marginBottom: "2rem", maxWidth: 1200, overflowY: "auto", maxHeight: "90vh" }}> */}
            <Container
                style={{
                    marginTop: "1rem",
                    marginBottom: "2rem",
                    maxWidth: 1200,
                    maxHeight: "80vh",
                    overflowY: "scroll",
                    scrollbarWidth: "none" // For Firefox
                }}
                className="hide-scrollbar"
            >
                {
                    isAlertInfoVisible &&
                    <Alert
                        variant="dark"
                        onClose={() => setIsAlertInfoVisible(false)}
                        dismissible
                        style={{ borderRadius: "10px", background: "rgba(255, 255, 255, .45)" }}
                    >
                        <p className="text-center">
                            <span className="about-tip-font">
                                Tip: Try changing width of the screen, or try rotating your phone! It has <b>three</b> different views!
                            </span>
                        </p>
                    </Alert>
                }
                {
                    errorMessage &&
                    <Alert variant="warning" onClose={() => setErrorMessage(null)} dismissible style={{ borderRadius: '20px' }}>
                        <Alert.Heading>Oh snap! Something doesn't seem right...</Alert.Heading>
                        <p>
                            {errorMessage}
                        </p>
                    </Alert>
                }
                {
                    retrievedBlogs && retrievedBlogs.length > 0 && !latestBlogsLoading &&
                    <>
                        <div className="mb-2 text-center font-cherry-bomb" style={{ fontSize: "2rem" }}>Latest Blogs Timeline</div>
                        <div style={{ display: "flex", justifyContent: "center", marginBottom: "3rem" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div className="mb-2 text-center font-cherry-bomb" style={{ fontWeight: "", fontSize: "1.5rem" }}>TOP - </div>
                            </div>
                            <button
                                className={`btn btn-outline-dark mx-1${formPageSize === 10 ? ' active' : ''}`}
                                onClick={() => { setFormPageSize(10); setContinuationToken(undefined); }}
                            >
                                10
                            </button>
                            <button
                                className={`btn btn-outline-dark mx-1${formPageSize === 20 ? ' active' : ''}`}
                                onClick={() => { setFormPageSize(20); setContinuationToken(undefined); }}
                            >
                                20
                            </button>
                            <button
                                className={`btn btn-outline-dark mx-1${formPageSize === 30 ? ' active' : ''}`}
                                onClick={() => { setFormPageSize(30); setContinuationToken(undefined); }}
                            >
                                30
                            </button>
                        </div>
                        <div className="d-flex">
                            {/* Left Pane: Category Hierarchy */}
                            <div className="category-hierarchy me-4" style={{ minWidth: 220, maxWidth: 260 }}>
                                <div className="category-hierarchy-title mb-3">Categories</div>
                                <ul className="category-list">
                                    {categoryHierarchy.map(catObj => (
                                        <li key={catObj.category}>
                                            <div className="category-name">
                                                <Badge bg="secondary" className="me-1">{catObj.category}</Badge>
                                            </div>
                                            <ul className="category-blog-list">
                                                {catObj.blogs.map(blog => (
                                                    <li key={blog.id} className="category-blog-title">
                                                        <span title={blog.formTitle}>{blog.formTitle}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {/* Right Pane: Timeline */}
                            {/* <div style={{ flex: 1 }}> */}
                            <div className="timeline">
                                {groupedBlogs.map((item, idx) => {
                                    if (item.type === "month") {
                                        return (
                                            <div key={item.value + idx} className="timeline-month-label">
                                                {item.value}
                                            </div>
                                        );
                                    }
                                    const blog = item.value;
                                    return (
                                        <div className="timeline-item" key={idx}>
                                            <div className="timeline-content">
                                                <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                                                    <div>
                                                        <Badge
                                                            key={blog.formCategory}
                                                            bg="info"
                                                            className="text-uppercase me-1 mb-1"
                                                        >
                                                            {blog.formCategory}
                                                        </Badge>
                                                        {/* {
                                                            blog.categories?.map(cat => (
                                                                <Badge
                                                                    key={cat}
                                                                    bg="info"
                                                                    className="text-uppercase me-1 mb-1"
                                                                >
                                                                    {cat}
                                                                </Badge>
                                                            ))
                                                        } */}
                                                    </div>
                                                    <span style={{ fontSize: "0.85rem", color: "#888" }}>
                                                        {formatDate(blog.formCreateDate)}
                                                    </span>
                                                </div>
                                                <Card className="shadow-sm">
                                                    <Nav.Link onClick={navigateTo.bind(this, idx, blog)}>
                                                        <Card.Body>
                                                            <Card.Title>{blog.formTitle}</Card.Title>
                                                            <Card.Text>{blog.formContent && blog.formContent.slice(0, 100)}</Card.Text>
                                                        </Card.Body>
                                                    </Nav.Link>
                                                    <Card.Footer>
                                                        <small className="text-muted">
                                                            Last updated: {formatDate(blog._ts)}
                                                        </small>
                                                    </Card.Footer>
                                                </Card>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="timeline-line" />
                            </div>
                            {/* </div> */}
                        </div>
                    </>
                }
                {
                    retrievedBlogs && retrievedBlogs.length === 0 && !latestBlogsLoading &&
                    <>
                        <div className="centered">
                            <Card style={{ borderWidth: "0" }}>
                                <Card.Body>
                                    <Card.Title
                                        className='font-cherry-bomb'
                                        style={{ textAlign: "center" }}>
                                        No blogs found...
                                    </Card.Title>
                                </Card.Body>
                                <Card.Img
                                    style={{
                                        borderBottomLeftRadius: "0",
                                        borderBottomRightRadius: "0",
                                        borderTopLeftRadius: "0",
                                        borderTopRightRadius: "0"
                                    }}
                                    src={'https://picsum.photos/800/400'} />
                            </Card>
                        </div>
                    </>
                }
                {
                    !retrievedBlogs &&
                    latestBlogsLoading === true &&
                    <>
                        <div className="centered">
                            <div className="centered font-cherry-bomb" style={{ color: "rgba(0, 0, 0, 0.5)" }}>Loading...</div>
                            <div className="lds-circle">
                                <div style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}></div>
                            </div>
                        </div>
                    </>
                }
            </Container>
            <style>{`
                .hide-scrollbar {
                    scrollbar-width: none; /* Firefox */
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none; /* Chrome, Safari, Opera */
                }
                .category-hierarchy {
                    background: #fff;
                    border-radius: 12px;
                    box-shadow: 0 2px 12px 0 #e0e7ef;
                    padding: 1.5rem 1rem;
                    min-height: 400px;
                    max-height: 80vh;
                    overflow-y: auto;
                }
                .category-hierarchy-title {
                    font-weight: bold;
                    font-size: 1.2rem;
                    color: #0d6efd;
                    letter-spacing: 1px;
                    border-bottom: 1px solid #e0e7ef;
                    padding-bottom: 0.5rem;
                }
                .category-list {
                    list-style: none;
                    padding-left: 0;
                    margin-bottom: 0;
                }
                .category-list > li {
                    margin-bottom: 1.2rem;
                }
                .category-name {
                    font-weight: 500;
                    margin-bottom: 0.2rem;
                }
                .category-blog-list {
                    list-style: none;
                    padding-left: 1.2rem;
                    margin-bottom: 0;
                }
                .category-blog-title {
                    font-size: 0.97rem;
                    color: #333;
                    margin-bottom: 0.15rem;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                }
                .timeline {
                    position: relative;
                    padding-left: 90px;
                }
                .timeline-line {
                    position: absolute;
                    left: 40px;
                    top: 0;
                    bottom: 0;
                    width: 6px;
                    background: linear-gradient(to bottom, #0dcaf0 0%, #b6c2d1 50%, #0d6efd 100%);
                    border-radius: 8px;
                    z-index: 0;
                    box-shadow: 0 0 12px 2px #b6c2d1, 0 0 0 4px #e0e7ef inset;
                    opacity: 0.85;
                    transition: background 0.5s;
                    overflow: hidden;
                }
                .timeline-line::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    height: 100%;
                    background: repeating-linear-gradient(
                        135deg,
                        rgba(255,255,255,0.12) 0px,
                        rgba(255,255,255,0.12) 8px,
                        transparent 8px,
                        transparent 16px
                    );
                    pointer-events: none;
                    z-index: 1;
                    animation: shimmer 2.5s linear infinite;
                }
                @keyframes shimmer {
                    0% { background-position: 0 0; }
                    100% { background-position: 40px 40px; }
                }
                .timeline-item {
                    position: relative;
                    margin-bottom: 40px;
                }
                .timeline-dot {
                    position: absolute;
                    left: 32px;
                    top: 18px;
                    width: 16px;
                    height: 16px;
                    background: #0dcaf0;
                    border-radius: 50%;
                    border: 3px solid #fff;
                    box-shadow: 0 0 0 2px #b6c2d1;
                    z-index: 1;
                }
                .timeline-content {
                    position: relative;
                    margin-left: 60px;
                }
                .timeline-month-label {
                    position: relative;
                    left: -60px;
                    width: 120px;
                    text-align: right;
                    font-weight: bold;
                    font-size: 1.1rem;
                    color: #0d6efd;
                    margin-bottom: 10px;
                    margin-top: 30px;
                    letter-spacing: 1px;
                }
                @media (max-width: 900px) {
                    .d-flex {
                        flex-direction: column;
                    }
                    .category-hierarchy {
                        // max-width: 100%;
                        // margin-bottom: 2rem;
                        display: none;
                    }
                }
                @media (max-width: 576px) {
                    .timeline-content {
                        margin-left: 0;
                    }
                    .timeline {
                        padding-left: 0;
                    }
                    .timeline-line, .timeline-dot, .timeline-month-label {
                        display: none;
                    }
                    .category-hierarchy {
                        padding: 1rem 0.5rem;
                    }
                }
            `}</style>
        </div>
    );
}

export default BlogsTimeline;